import { useProject } from "@/hooks/use-project";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { MapView, MapViewMarker, type Marker } from "@msuite/ui/map";
import type { Row } from "@msuite/ui/table";
import { getGeometryCoordinates } from "@msuite/utils/misc";
import { guardString } from "@msuite/utils/strings";
import type { OrganizationAttendanceListItem } from "@msuite/utils/types";
import { useMemo } from "react";

/** Props Interface */
interface UserAttendancesDetailsRowProps {
	row: Row<OrganizationAttendanceListItem>;
}

export function UserAttendancesDetailsRow({ row }: UserAttendancesDetailsRowProps) {
	/** Hooks */
	const { project } = useProject({ projectId: row.original.projectId });

	/** Memoized */
	const markers = useMemo(() => {
		const markers: Marker[] = [];

		if (row.original.startCoordinates) {
			const coordinates = getGeometryCoordinates(row.original.startCoordinates);
			if (coordinates.longitude && coordinates.latitude) {
				markers.push({
					longitude: coordinates.longitude,
					latitude: coordinates.latitude,
					render: () => (
						<MapViewMarker
							iconName="Timer"
							color="var(--color-emerald-500)"
						/>
					),
				});
			}
		}

		if (row.original.endCoordinates) {
			const coordinates = getGeometryCoordinates(row.original.endCoordinates);
			if (coordinates.longitude && coordinates.latitude) {
				markers.push({
					longitude: coordinates.longitude,
					latitude: coordinates.latitude,
					render: () => (
						<MapViewMarker
							iconName="Timer"
							color="var(--color-red-500)"
						/>
					),
				});
			}
		}

		if (project?.geometry) {
			const coordinates = getGeometryCoordinates(project?.geometry);
			if (coordinates.longitude && coordinates.latitude) {
				markers.push({
					longitude: coordinates.longitude,
					latitude: coordinates.latitude,
					render: () => (
						<MapViewMarker
							iconName="Folder"
							color="var(--color-accent-500)"
						/>
					),
				});
			}
		}

		return markers;
	}, [row.original.startCoordinates, row.original.endCoordinates, project]);

	/** Guard */
	if (!markers.length) {
		return (
			<div className="p-4">
				<BlankSlate />
			</div>
		);
	}

	/** UserAttendancesDetailsRowProps */
	return (
		<div className="w-full p-4 h-[300px]">
			<div className="card overflow p-0 h-full w-full">
				<MapView
					apiKey={guardString(process.env.PUBLIC_GOOGLE_MAPS_API_KEY)}
					mapId={process.env.PUBLIC_GOOGLE_MAPS_MAP_ID!}
					markers={markers}
					defaultZoom={18}
					disableClustering
				/>
			</div>
		</div>
	);
}
